import { useCallback, useEffect, useRef, useState } from "react";
import { getErrorMessage } from "../utils/errors";

export interface Action<A extends unknown[], R> {
  run: (...args: A) => Promise<R | undefined>;
  pending: boolean;
  error: string | undefined;
  clearError: () => void;
}

/** Wraps a mutation with pending/error state. `run` resolves to undefined on failure. */
export const useAction = <A extends unknown[], R>(
  action: (...args: A) => Promise<R>
): Action<A, R> => {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(
    async (...args: A): Promise<R | undefined> => {
      setPending(true);
      setError(undefined);
      try {
        return await action(...args);
      } catch (err) {
        if (mounted.current) {
          setError(getErrorMessage(err));
        }
        return undefined;
      } finally {
        if (mounted.current) {
          setPending(false);
        }
      }
    },
    [action]
  );

  const clearError = useCallback(() => setError(undefined), []);
  return { run, pending, error, clearError };
};
